import type { ApolloError } from '@apollo/client';

/** Access token expired — `createTokenRefreshLink` renews it and replays the request. */
export const EXPIRED_TOKEN_CODE = 'AHEEX';

/** Access token not recognised — `createTokenRefreshLink` tries the refresh token before logging out. */
export const AUTHENTICATION_REQUIRED_CODE = 'AHERA';

/** `TotemError` answers this for `AHERA`; some paths surface the message without the code. */
export const AUTHENTICATION_REQUIRED_MESSAGE = 'Authentification requise';

/** Account closed — the error handler clears the token and redirects to /sven. */
export const BANNED_CODE = 'AHEBANNED';
export const ARCHIVED_CODE = 'AHEARCHIVED';

/** Network error used to abort the requests queued behind a refresh that ultimately failed. */
export const SESSION_EXPIRED_MESSAGE = 'Session expired';

/**
 * Refresh impossible faute de réponse du serveur (API qui redémarre, 5xx, coupure). Distinct de
 * `SESSION_EXPIRED_MESSAGE` à dessein : ce n'est PAS une erreur d'authentification, la session est
 * conservée, et l'erreur doit atteindre le boundary pour que l'utilisateur en soit informé.
 */
export const NETWORK_FAILURE_MESSAGE = 'Connexion au serveur perdue, la session est conservée. Réessayez.';

/**
 * An expired or absent session, which the Apollo chain already handles with a token refresh or a
 * logout. Keeps it away from the `ErrorHandler` boundary, which would replace the login page with
 * the "Oups, il y a eu une petite erreur..." screen.
 */
export function isAuthenticationError(error?: Pick<ApolloError, 'graphQLErrors' | 'networkError'> | null): boolean {
    if (!error) {
        return false;
    }

    return (
        error.graphQLErrors?.some(({ extensions }) =>
            [EXPIRED_TOKEN_CODE, AUTHENTICATION_REQUIRED_CODE].includes(extensions?.code as string),
        ) || error.networkError?.message === SESSION_EXPIRED_MESSAGE
    );
}
